import React, { ChangeEvent, MouseEvent, useRef } from 'react';
import { useIntl } from '@leagueplatform/locales';
import { toastActions, TOAST_STATUS } from '@leagueplatform/toast-messages';
import {
  Modal,
  StackLayout,
  Box,
  Button,
  styled,
} from '@leagueplatform/genesis-core';
import { useUploadProfilePhoto } from '../../../hooks/use-upload-profile-photo.hook';
import { usePersonalInformation } from '../../../hooks/use-personal-information.hook';
import { useUpdateUserProfile } from '../../../hooks/use-update-user-profile.hook';
import { useGetAssetAsBlob } from '../../../hooks/use-get-asset-as-blob.hook';
// import { ProfileImageErrorModal } from 'components/modals/profile-image-error-modal.component';

const InvisibleInput = styled('input', {
  display: 'none',
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
});

export const EditProfilePhoto = () => {
  const { formatMessage } = useIntl();

  const { data } = usePersonalInformation();

  const [
    { profilePhotoId, uploadError },
    { setUploadError, uploadProfilePhoto, resetProfilePhotoId },
  ] = useUploadProfilePhoto(data);

  const { updateUserProfile } = useUpdateUserProfile({
    onSuccess: () => {
      toastActions.add({
        type: TOAST_STATUS.SUCCESS,
        textId: 'PROFILE_PHOTO_UPDATED',
      });
    },
    onError: () => {
      resetProfilePhotoId();
      toastActions.add({
        type: TOAST_STATUS.ERROR,
        textId: 'PROFILE_PHOTO_UPDATE_FAILED',
      });
    },
  });

  const { data: profileImageBlob } = useGetAssetAsBlob(profilePhotoId);

  const initials = data?.userInitials;
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      <Modal.Root open={uploadError} onOpenChange={() => setUploadError(false)}>
        {/* <ProfileImageErrorModal /> */}
      </Modal.Root>
      <StackLayout horizontalAlignment="center">
        <Box css={{ position: 'relative' }}>
          <Box
            data-testid="profile-image"
            className={profilePhotoId ? 'background-image' : ''}
            css={{
              width: 128,
              height: 128,
              borderRadius: '$circle',
              backgroundColor: '$surfaceCardPrimary',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              typography: '$headingOne',
              ...(profilePhotoId && {
                backgroundImage: `url(${profileImageBlob})`,
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: '50%',
              }),
            }}
          >
            {!profilePhotoId && initials}
          </Box>
          <InvisibleInput
            id="photo-file-input"
            ref={inputRef}
            type="file"
            accept="image/*"
            data-testid="file-input"
            onChange={async (e: ChangeEvent<HTMLInputElement>) => {
              if (!e.currentTarget.files) return;
              const file = e?.currentTarget?.files[0];
              if (file) {
                const newID = await uploadProfilePhoto(file);
                if (newID)
                  updateUserProfile({
                    avatar: { imageId: newID },
                  });
              }
            }}
            onClick={(e: MouseEvent<HTMLInputElement>) => {
              (e.target as HTMLInputElement).value = '';
            }}
          />
          <Button
            hideLabel
            icon="interfaceEdit"
            size="small"
            onClick={() => inputRef?.current?.click()}
            css={{ position: 'absolute', bottom: 0, right: '$half' }}
          >
            {formatMessage({
              id: profilePhotoId ? 'UPDATE_PROFILE_PHOTO' : 'ADD_PROFILE_PHOTO',
            })}
          </Button>
        </Box>
      </StackLayout>
    </>
  );
};
